import { useState } from "react";
import { ar } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { LANG, tr } from "../lib/i18n";

const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const DAYS_AR = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => { const [y, m, d] = (s || "").split("-").map(Number); return y && m ? new Date(y, m - 1, d || 1) : undefined; };

// Arabic month/day names, Western digits, right-to-left
const formatters = {
  formatCaption: (d) => `${MONTHS_AR[d.getMonth()]} ${d.getFullYear()}`,
  formatWeekdayName: (d) => DAYS_AR[d.getDay()],
  formatDay: (d) => String(d.getDate()),
};

function MonthPicker({ value, onChange, className, testid }) {
  const [y, m] = (value || "").split("-");
  const year = Number(y) || new Date().getFullYear();
  const emit = (yy, mm) => onChange({ target: { value: `${yy}-${pad(mm)}` } });
  return (
    <span className={`inline-flex gap-1 ${className || ""}`} dir="rtl" data-testid={testid}>
      <select className="input" value={Number(m) || ""} onChange={(e) => emit(year, e.target.value)} data-testid={testid && `${testid}-m`}>
        {MONTHS_AR.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
      </select>
      <input className="input" type="number" style={{ width: 90 }} value={year} onChange={(e) => emit(e.target.value, Number(m) || 1)} data-testid={testid && `${testid}-y`} />
    </span>
  );
}

/** Drop-in for <input type="date|month">: native in English, Arabic calendar in Arabic. onChange gets {target:{value}}. */
export function DateInput({ type = "date", value, onChange, className, "data-testid": testid, ...rest }) {
  const [open, setOpen] = useState(false);
  if (LANG !== "ar") return <input type={type} value={value || ""} onChange={onChange} className={className} data-testid={testid} {...rest} />;
  if (type === "month") return <MonthPicker value={value} onChange={onChange} className={className} testid={testid} />;
  const d = parse(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${className || "input"} text-right whitespace-nowrap`} style={{ minWidth: 150 }} data-testid={testid}>
          {d ? `${d.getDate()} ${MONTHS_AR[d.getMonth()]} ${d.getFullYear()}` : <span className="opacity-60">{tr("x.choose_date")}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" dir="rtl">
        <Calendar mode="single" dir="rtl" locale={ar} formatters={formatters} selected={d} defaultMonth={d} className="p-3 cal-noflip"
          classNames={{ day_today: "!font-bold underline", head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.62rem]", nav_button_previous: "absolute right-1", nav_button_next: "absolute left-1" }}
          onSelect={(x) => { onChange({ target: { value: x ? toIso(x) : "" } }); setOpen(false); }} />
        {value ? <button type="button" className="w-full text-xs py-2 border-t" onClick={() => { onChange({ target: { value: "" } }); setOpen(false); }}>{tr("x.clear")}</button> : null}
      </PopoverContent>
    </Popover>
  );
}
